import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import {
  Upload,
  FileText,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Scale,
  ArrowRight,
  Loader2,
  Check,
  Lock,
  Crown,
  Download,
  Info,
  AlertCircle,
  Copy,
  Mail,
  MapPin,
  RefreshCw,
  X,
  FileUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AvvoWidget } from "@/components/AvvoWidget";
import { logReferralClick } from "@/utils/referral.functions";

import { useSubscription } from "@/hooks/use-subscription";
import { analyzeContract } from "@/utils/analyzer.functions";
import { exportAnalysisPdf, exportLawyerReport } from "@/lib/pdf-export";
import { saveAnalysisAsVersion } from "@/lib/contracts.functions";
import { preprocessImageFile } from "@/utils/image-preprocess";
import { loadPdf, renderPdfPageToDataUrl } from "@/utils/pdf-render";

type Severity = "info" | "caution" | "warning";
export type AnalysisResult = {
  verdict: { summary: string; risk: "low" | "medium" | "high" };
  riskScore: number;
  keyTerms: string[];
  redFlags: string[];
  clauseExplanations: Array<{
    title: string;
    what: string;
    why: string;
    suggestion: string;
    negotiationScript?: string;
    severity: Severity;
  }>;
};

const SAMPLE_TEXT = `RESIDENTIAL LEASE AGREEMENT

This Lease Agreement is made between Landlord and Tenant for the property at 123 Main St, Apt 4B. Term: 12 months at $2,400/month. Security deposit: $4,800 (two months). Late fee: $75 after a 5-day grace period. Landlord may enter the unit with 12 hours notice. Tenant is responsible for all repairs under $500. Early termination requires payment equal to three months rent. Lease auto-renews month-to-month at $2,640/mo unless tenant gives 60 days written notice.`;

type StepState = "pending" | "active" | "done" | "error";

function ProgressStepper({
  extracting,
  analyzing,
  hasResult,
  errorAt,
  subLabel,
  progress,
}: {
  extracting: null | "pdf" | "image";
  analyzing: boolean;
  hasResult: boolean;
  errorAt?: "extract" | "analyze" | null;
  subLabel?: string | null;
  progress?: number | null;
}) {
  if (!extracting && !analyzing && !hasResult && !errorAt) return null;
  const extractDone = analyzing || hasResult;
  const extractState: StepState =
    errorAt === "extract" ? "error" : extracting ? "active" : extractDone ? "done" : "pending";
  const analyzeState: StepState =
    errorAt === "analyze" ? "error" : analyzing ? "active" : hasResult ? "done" : "pending";
  const resultState: StepState = hasResult ? "done" : "pending";
  const steps: { key: string; label: string; state: StepState }[] = [
    {
      key: "extract",
      label: extracting === "image" ? "Scanning image" : "Extracting text",
      state: extractState,
    },
    { key: "analyze", label: "Analyzing clauses", state: analyzeState },
    { key: "results", label: "Results ready", state: resultState },
  ];
  const showBar = (extracting || analyzing) && typeof progress === "number";
  return (
    <div
      className="mt-4 rounded-xl border border-border bg-background/60 p-3"
      role="status"
      aria-live="polite"
    >
      <ol className="flex items-center gap-2 sm:gap-3">
        {steps.map((step, i) => (
          <li key={step.key} className="flex flex-1 items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-colors",
                  step.state === "done" && "bg-success text-success-foreground",
                  step.state === "active" && "bg-primary text-primary-foreground",
                  step.state === "error" && "bg-destructive text-destructive-foreground",
                  step.state === "pending" && "bg-muted text-muted-foreground",
                )}
              >
                {step.state === "done" ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                ) : step.state === "active" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : step.state === "error" ? (
                  <AlertCircle className="h-3.5 w-3.5" />
                ) : (
                  i + 1
                )}
              </span>
              <span
                className={cn(
                  "text-xs font-medium sm:text-sm",
                  step.state === "pending" && "text-muted-foreground",
                  step.state === "error" && "text-destructive",
                  step.state !== "pending" && step.state !== "error" && "text-foreground",
                )}
              >
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={cn(
                  "hidden h-px flex-1 transition-colors sm:block",
                  step.state === "done" ? "bg-success" : "bg-border",
                )}
              />
            )}
          </li>
        ))}
      </ol>
      {(subLabel || showBar) && (
        <div className="mt-2.5 space-y-1.5">
          {subLabel && <p className="text-xs text-muted-foreground">{subLabel}</p>}
          {showBar && (
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${Math.max(4, Math.min(100, progress!))}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Friendly classification of extract/OCR failures.
type ExtractFailKind = "size" | "network" | "rate" | "credits" | "unreadable" | "auth" | "unknown";
function classifyExtractError(
  err: any,
  msgFallback?: string,
): { kind: ExtractFailKind; message: string; retryable: boolean } {
  const raw = (err?.message || msgFallback || "").toString().toLowerCase();
  if (raw.includes("too large") || raw.includes("413"))
    return {
      kind: "size",
      message: "This file is over our size limit. Try a smaller PDF (≤15MB) or image (≤8MB).",
      retryable: false,
    };
  if (raw.includes("rate") || raw.includes("429"))
    return {
      kind: "rate",
      message: "We're a bit busy right now. Hang tight — we'll keep trying.",
      retryable: true,
    };
  if (raw.includes("credit") || raw.includes("402"))
    return {
      kind: "credits",
      message: "Our AI credits are temporarily out. Please try again shortly.",
      retryable: true,
    };
  if (raw.includes("unreadable") || raw.includes("couldn't read") || raw.includes("could not read"))
    return {
      kind: "unreadable",
      message:
        "We couldn't make out any text. A sharper, well-lit photo (or a clearer scan) usually fixes this.",
      retryable: true,
    };
  if (raw.includes("unauthorized") || raw.includes("401"))
    return {
      kind: "auth",
      message: "Your session expired. Sign in again to continue.",
      retryable: false,
    };
  if (
    raw.includes("failed to fetch") ||
    raw.includes("network") ||
    raw.includes("timeout") ||
    raw.includes("aborted") ||
    raw.includes("503") ||
    raw.includes("502") ||
    raw.includes("504")
  )
    return { kind: "network", message: "Connection hiccup — we'll keep trying.", retryable: true };
  return {
    kind: "unknown",
    message: "Something went wrong reading that file. Try again, or upload a different copy.",
    retryable: true,
  };
}

// Configurable auto-retry policy for transient extraction failures.
// Per-kind max attempts (number of automatic retries after the initial try)
// and base backoff in ms; backoff grows exponentially with jitter.
const EXTRACT_RETRY_POLICY: Record<ExtractFailKind, { maxRetries: number; baseDelayMs: number }> = {
  network: { maxRetries: 3, baseDelayMs: 800 },
  rate: { maxRetries: 3, baseDelayMs: 2500 },
  credits: { maxRetries: 1, baseDelayMs: 4000 },
  unreadable: { maxRetries: 0, baseDelayMs: 0 },
  size: { maxRetries: 0, baseDelayMs: 0 },
  auth: { maxRetries: 0, baseDelayMs: 0 },
  unknown: { maxRetries: 1, baseDelayMs: 1000 },
};

function nextBackoffMs(kind: ExtractFailKind, attempt: number): number {
  const { baseDelayMs } = EXTRACT_RETRY_POLICY[kind];
  const exp = baseDelayMs * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exp;
  return Math.min(15000, Math.round(exp + jitter));
}

export function ContractAnalyzer({ toolSlug }: { toolSlug?: string } = {}) {
  // toolSlug is forwarded to ResultsGrid via a closure prop below.
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [stateContext, setStateContext] = useState("");
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [analysisMeta, setAnalysisMeta] = useState<{
    analysisId: string | null;
    extractedText: string;
  } | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [extracting, setExtracting] = useState<null | "pdf" | "image">(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorAt, setErrorAt] = useState<null | "extract" | "analyze">(null);
  const [extractError, setExtractError] = useState<{
    kind: ExtractFailKind;
    message: string;
    retryable: boolean;
  } | null>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);
  const [subLabel, setSubLabel] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const cancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });
  const dragDepth = useRef(0);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const getAccessTokenOrRedirect = async (message = "Sign in to upload documents.") => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      toast.info(message);
      navigate({ to: "/auth", search: { redirect: "/#analyzer" } as never });
      return null;
    }
    return token;
  };

  const handleAnalyze = async () => {
    if (!text.trim() || analyzing) return;
    const accessToken = await getAccessTokenOrRedirect(
      "Sign in to analyze contracts — your free quota stays with your account.",
    );
    if (!accessToken) return;
    if (text.trim().length < 40) {
      toast.error("Paste a bit more contract text (40+ characters).");
      return;
    }
    setAnalyzing(true);
    setResult(null);
    setErrorAt(null);
    try {
      const res = await analyzeContract({
        data: { text, toolSlug, stateContext: stateContext || undefined },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        if (res.reason === "quota_exceeded") {
          toast.error(
            "You have run out of contract analysis credits. Purchase a credit pack to analyze this document.",
          );
          navigate({ to: "/pricing" });
          return;
        }
        if (res.reason === "rate_limited") {
          toast.error("We're a bit busy — try again in a moment.");
          return;
        }
        toast.error("Couldn't analyze that document.");
        return;
      }
      setResult(res.result);
      setAnalysisMeta({
        analysisId: (res as any).analysisId ?? null,
        extractedText: (res as any).extractedText ?? text,
      });
      setTimeout(() => {
        document.getElementById("results")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err: any) {
      console.error(err);
      setErrorAt("analyze");
      if (err instanceof Response && err.status === 401) {
        toast.info("Please sign in again to analyze this document.");
        navigate({ to: "/auth", search: { redirect: "/#analyzer" } as never });
        return;
      }
      toast.error(err?.message ?? "Analysis failed. Try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const loadSample = () => setText(SAMPLE_TEXT);

  const ocrDataUrl = async (dataUrl: string): Promise<string> => {
    const accessToken = await getAccessTokenOrRedirect();
    if (!accessToken) throw new Error("Please sign in to scan images.");
    const { data, error } = await supabase.functions.invoke("ocr-image", {
      body: { imageDataUrl: dataUrl },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (error) throw error;
    const t = (data?.text ?? "").trim();
    if (!t || t === "UNREADABLE") return "";
    return t;
  };

  const handleExtractFailure = (
    err: any,
    _kind: "pdf" | "image",
    attempt: number,
    retry: () => void,
    fallbackMsg?: string,
  ) => {
    console.error(err);
    const info = classifyExtractError(err, fallbackMsg);
    if (info.kind === "auth") {
      toast.info(info.message);
      navigate({ to: "/auth", search: { redirect: "/#analyzer" } as never });
      return;
    }
    const policy = EXTRACT_RETRY_POLICY[info.kind];
    if (info.retryable && attempt < policy.maxRetries) {
      const nextAttempt = attempt + 1;
      const delay = nextBackoffMs(info.kind, attempt);
      toast.message(`Retrying (${nextAttempt}/${policy.maxRetries})…`, {
        description: info.message,
      });
      setSubLabel(`Retrying — attempt ${nextAttempt} of ${policy.maxRetries}…`);
      setTimeout(() => {
        if (!cancelRef.current.cancelled) retry();
      }, delay);
      return;
    }
    setErrorAt("extract");
    setExtractError(info);
    toast.error(
      info.retryable
        ? `${info.message} (gave up after ${policy.maxRetries + 1} tries)`
        : info.message,
    );
  };

  const processPdf = async (file: File, attempt = 0) => {
    const accessToken = await getAccessTokenOrRedirect();
    if (!accessToken) return;
    if (file.size > 15 * 1024 * 1024) {
      const info = {
        kind: "size" as const,
        message: "PDF too large — please upload a file under 15MB.",
        retryable: false,
      };
      setErrorAt("extract");
      setExtractError(info);
      toast.error(info.message);
      return;
    }
    cancelRef.current = { cancelled: false };
    setExtracting("pdf");
    setErrorAt(null);
    setExtractError(null);
    setLastFile(file);
    setSubLabel(attempt > 0 ? `Retrying (attempt ${attempt + 1})…` : "Reading PDF…");
    setProgress(5);
    try {
      const buf = await file.arrayBuffer();
      let binary = "";
      const bytes = new Uint8Array(buf);
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk)
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
      const pdfBase64 = btoa(binary);
      if (cancelRef.current.cancelled) return;
      setProgress(20);
      const { data, error } = await supabase.functions.invoke("extract-pdf", {
        body: { pdfBase64 },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (cancelRef.current.cancelled) return;
      if (error) throw error;
      const extracted = (data?.text ?? "").trim();
      if (extracted && !data?.scanned) {
        setProgress(100);
        setText(extracted);
        toast.success(`Loaded ${file.name}`);
        return;
      }
      setSubLabel("Scanned PDF detected — running OCR on pages…");
      setProgress(25);
      const pdfDoc = await loadPdf(file);
      const pageCount = Math.min(pdfDoc.numPages, 8);
      const parts: string[] = [];
      for (let p = 1; p <= pageCount; p++) {
        if (cancelRef.current.cancelled) return;
        setSubLabel(`OCR page ${p} of ${pageCount}…`);
        const dataUrl = await renderPdfPageToDataUrl(pdfDoc, p, 1600);
        const t = await ocrDataUrl(dataUrl);
        if (t) parts.push(t);
        setProgress(25 + Math.round((p / pageCount) * 70));
      }
      const joined = parts.join("\n\n").trim();
      if (!joined) {
        handleExtractFailure(
          new Error("UNREADABLE"),
          "pdf",
          attempt,
          () => void processPdf(file, attempt + 1),
        );
        return;
      }
      setText(joined.slice(0, 40000));
      setProgress(100);
      toast.success(`Extracted ${pageCount} page${pageCount > 1 ? "s" : ""} via OCR`);
    } catch (err: any) {
      if (cancelRef.current.cancelled) return;
      handleExtractFailure(
        err,
        "pdf",
        attempt,
        () => void processPdf(file, attempt + 1),
        "Failed to read PDF.",
      );
    } finally {
      setExtracting(null);
      setSubLabel(null);
      setProgress(null);
    }
  };

  const processImage = async (file: File, attempt = 0) => {
    const accessToken = await getAccessTokenOrRedirect();
    if (!accessToken) return;
    if (file.size > 8 * 1024 * 1024) {
      const info = {
        kind: "size" as const,
        message: "Image too large — please use a photo under 8MB.",
        retryable: false,
      };
      setErrorAt("extract");
      setExtractError(info);
      toast.error(info.message);
      return;
    }
    cancelRef.current = { cancelled: false };
    setExtracting("image");
    setErrorAt(null);
    setExtractError(null);
    setLastFile(file);
    setSubLabel(attempt > 0 ? `Retrying (attempt ${attempt + 1})…` : "Preparing image…");
    setProgress(10);
    try {
      const { dataUrl, report } = await preprocessImageFile(file, {
        onStage: ({ label, progress: stagePct }) => {
          if (cancelRef.current.cancelled) return;
          setSubLabel(label);
          // Map preprocess 0-100 into the 10-45 band of the overall progress bar.
          setProgress(10 + Math.round((stagePct / 100) * 35));
        },
      });
      if (cancelRef.current.cancelled) return;
      setProgress(45);
      const bits: string[] = [];
      if (Math.abs(report.rotated) >= 0.5) bits.push(`straightened ${report.rotated}°`);
      if (report.cropped) bits.push("cropped borders");
      setSubLabel(`Cleaned up image${bits.length ? ` (${bits.join(", ")})` : ""} — running OCR…`);
      setProgress(60);
      const ocrText = await ocrDataUrl(dataUrl);
      if (cancelRef.current.cancelled) return;
      setProgress(95);
      if (!ocrText) {
        handleExtractFailure(
          new Error("UNREADABLE"),
          "image",
          attempt,
          () => void processImage(file, attempt + 1),
        );
        return;
      }
      setText(ocrText.slice(0, 40000));
      setProgress(100);
      toast.success("Text extracted from image");
    } catch (err: any) {
      if (cancelRef.current.cancelled) return;
      handleExtractFailure(
        err,
        "image",
        attempt,
        () => void processImage(file, attempt + 1),
        "OCR failed.",
      );
    } finally {
      setExtracting(null);
      setSubLabel(null);
      setProgress(null);
    }
  };

  const cancelExtraction = () => {
    cancelRef.current.cancelled = true;
    setExtracting(null);
    setSubLabel(null);
    setProgress(null);
    toast.message("Extraction cancelled.");
  };

  const handlePdfSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void processPdf(file);
  };
  const handleImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void processImage(file);
  };
  const handleDroppedFile = (file: File) => {
    if (extracting !== null) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    const isImage = file.type.startsWith("image/");
    if (isPdf) void processPdf(file);
    else if (isImage) void processImage(file);
    else toast.error("Unsupported file. Drop a PDF or image.");
  };
  const onDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleDroppedFile(file);
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const file = (e as CustomEvent<{ file: File }>).detail?.file;
      if (file) handleDroppedFile(file);
    };
    window.addEventListener("legald:upload", handler);
    // Pick up a file dropped before this lazy component mounted.
    const pending = (window as any).__legaldPendingFile as File | undefined;
    if (pending) {
      (window as any).__legaldPendingFile = undefined;
      handleDroppedFile(pending);
    }
    return () => window.removeEventListener("legald:upload", handler);
  }, []);

  return (
    <div className="w-full">
      <div
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          "relative rounded-2xl border bg-card p-6 shadow-card sm:p-8 transition-all duration-200",
          isDragging
            ? "border-primary border-2 border-dashed bg-primary/5 scale-[1.01] shadow-elegant ring-4 ring-primary/20"
            : "border-border",
        )}
      >
        {isDragging && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-background/80 backdrop-blur-sm animate-fade-in">
            <div className="flex flex-col items-center gap-2 text-primary animate-scale-in">
              <Upload className="h-12 w-12 animate-bounce" />
              <p className="text-base font-semibold">Drop to analyze</p>
              <p className="text-xs text-muted-foreground">PDF or image · 15MB max</p>
            </div>
          </div>
        )}
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={handlePdfSelected}
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleImageSelected}
        />

        {/* Prominent privacy reassurance — shown right where users are about to upload */}
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-success/30 bg-success/5 p-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success/15">
            <Lock className="h-4 w-4 text-success" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              Your document is private. We don't store it.
            </p>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              Encrypted in transit · Analyzed in-memory · Discarded immediately after the result is
              shown · Never used to train AI.
            </p>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-3">
          <Button
            variant="outline"
            size="default"
            className="gap-2"
            onClick={() => pdfInputRef.current?.click()}
            disabled={extracting !== null}
          >
            {extracting === "pdf" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {extracting === "pdf" ? "Reading PDF…" : "Upload PDF"}
          </Button>
          <Button
            variant="outline"
            size="default"
            className="gap-2"
            onClick={() => imageInputRef.current?.click()}
            disabled={extracting !== null}
          >
            {extracting === "image" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {extracting === "image" ? "Scanning…" : "Scan / Photo"}
          </Button>
          <button
            onClick={loadSample}
            className="ml-auto text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Try a sample lease →
          </button>
        </div>

        <details className="mb-4 group rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-foreground/90 [&::-webkit-details-marker]:hidden">
            <span className="flex items-center gap-2 font-medium">
              <Info className="h-4 w-4 text-primary" />
              Tips for best scan results
            </span>
            <span className="text-xs text-muted-foreground transition-transform group-open:rotate-180">
              ▾
            </span>
          </summary>
          <ul className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>
                <strong className="text-foreground">Sharp focus.</strong> Tap your camera to focus
                before snapping; avoid motion blur.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>
                <strong className="text-foreground">Even lighting.</strong> Daylight or a desk lamp
                works best — no harsh shadows or glare.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>
                <strong className="text-foreground">Straight on.</strong> Hold the phone parallel to
                the page; we'll auto-deskew small angles.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>
                <strong className="text-foreground">Whole page in frame.</strong> Include all four
                edges; crop tight but don't clip text.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>
                <strong className="text-foreground">High resolution.</strong> 1500px+ on the long
                edge. Most modern phone cameras qualify.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>
                <strong className="text-foreground">Plain background.</strong> Place pages on a
                contrasting flat surface (e.g. dark desk).
              </span>
            </li>
            <li className="flex items-start gap-2 sm:col-span-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>
                <strong className="text-foreground">PDF preferred.</strong> If you have a digital
                PDF, upload that instead of a photo — it's faster and more accurate. Limits: PDF
                ≤15MB, image ≤8MB.
              </span>
            </li>
          </ul>
        </details>

        <div className="mb-3 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <select
            value={stateContext}
            onChange={(e) => setStateContext(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Any State / General</option>
            <option value="California">California</option>
            <option value="New York">New York</option>
            <option value="Texas">Texas</option>
            <option value="Florida">Florida</option>
            <option value="Illinois">Illinois</option>
          </select>
          <span className="text-xs text-muted-foreground">
            For more accurate risk flagging (optional)
          </span>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="…or paste your contract text here. Lease agreements, NDAs, freelance contracts, and more."
          className="min-h-[180px] w-full resize-y rounded-xl border border-input bg-background p-4 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20"
        />

        <ProgressStepper
          extracting={extracting}
          analyzing={analyzing}
          hasResult={!!result}
          errorAt={errorAt}
          subLabel={analyzing ? "Reviewing clauses with AI…" : subLabel}
          progress={progress}
        />

        {extracting && (
          <div className="mt-2 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={cancelExtraction}
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </Button>
          </div>
        )}

        {errorAt && (
          <div
            className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 p-3 animate-fade-in"
            role="alert"
          >
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">
                  {errorAt === "extract"
                    ? "We couldn't read that file."
                    : "Analysis didn't go through."}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-destructive/80">
                  {errorAt === "extract"
                    ? (extractError?.message ?? "Try again, or upload a different copy.")
                    : "We hit a snag analyzing your contract. Let's try again."}
                </p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
              {errorAt === "extract" && extractError?.kind !== "size" && lastFile && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => handleDroppedFile(lastFile)}
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Try again
                </Button>
              )}
              {errorAt === "extract" && (
                <Button
                  variant="default"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    setErrorAt(null);
                    setExtractError(null);
                    setLastFile(null);
                    // Re-open the matching picker; default to PDF, image button still available below.
                    pdfInputRef.current?.click();
                  }}
                >
                  <FileUp className="h-3.5 w-3.5" /> Upload a different file
                </Button>
              )}
              {errorAt === "analyze" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => void handleAnalyze()}
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Retry analysis
                </Button>
              )}
            </div>
          </div>
        )}

        <Button
          variant="hero"
          size="xl"
          className="mt-4 w-full gap-2"
          onClick={handleAnalyze}
          disabled={analyzing}
        >
          {analyzing ? (
            <>
              <Sparkles className="h-5 w-5 animate-pulse" />
              Decoding your contract…
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              Analyze Now
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </Button>

        <p className="mt-3 text-center text-xs text-muted-foreground">
          🔒 Your document is analyzed in-memory and never stored. Zero data retention.
        </p>
      </div>

      {result && (
        <ResultsGrid
          result={result}
          toolSlug={toolSlug}
          analysisId={analysisMeta?.analysisId ?? null}
          extractedText={analysisMeta?.extractedText ?? text}
        />
      )}
    </div>
  );
}

function ResultsGrid({
  result,
  toolSlug,
  analysisId,
  extractedText,
}: {
  result: AnalysisResult;
  toolSlug?: string;
  analysisId?: string | null;
  extractedText?: string;
}) {
  const { isPro } = useSubscription();
  const riskConfig = {
    low: {
      color: "text-success",
      bg: "bg-success/10",
      border: "border-success/30",
      label: "Low Risk",
    },
    medium: {
      color: "text-warning",
      bg: "bg-warning/10",
      border: "border-warning/30",
      label: "Medium Risk",
    },
    high: {
      color: "text-destructive",
      bg: "bg-destructive/10",
      border: "border-destructive/30",
      label: "High Risk",
    },
  }[result.verdict.risk];

  const handleExport = () => {
    if (!isPro) return;
    try {
      exportAnalysisPdf(result);
      toast.success("PDF downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't export PDF.");
    }
  };

  const handleLawyerReport = async () => {
    try {
      await exportLawyerReport(result, { toolSlug: toolSlug ?? null });
      toast.success("Lawyer briefing downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't export the briefing.");
    }
  };

  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState<string | null>(null);

  const handleSaveAsVersion = async () => {
    if (!isPro) {
      toast.info("Saving to Clause History is a Pro feature.");
      return;
    }
    if (!analysisId) {
      toast.error("This analysis can't be saved (missing reference).");
      return;
    }
    setSaving(true);
    try {
      const res = await saveAnalysisAsVersion({
        data: {
          analysisId,
          extractedText: extractedText?.slice(0, 80000),
          toolSlug,
        },
      });
      if (!res.ok) {
        toast.error("Couldn't save to Clause History.");
        return;
      }
      setSavedTo(res.contractId);
      toast.success(`Saved as version ${res.versionNumber}.`);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save to Clause History.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="results" className="mt-12 scroll-mt-24 animate-fade-in">
      <ProgressStepper extracting={null} analyzing={false} hasResult={true} />
      <div className="mb-6 mt-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold tracking-tight">Analysis Results</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handleLawyerReport}>
            <Download className="h-4 w-4" /> Export report (PDF)
          </Button>
          {analysisId &&
            (savedTo ? (
              <Button variant="outline" size="sm" className="gap-2" asChild>
                <Link to="/clause-history/$contractId" params={{ contractId: savedTo }}>
                  <FileText className="h-4 w-4" /> View in Clause History
                </Link>
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleSaveAsVersion}
                disabled={saving}
                title={isPro ? "Save this analysis as a tracked version" : "Pro feature"}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                Save to Clause History
                {!isPro && <Lock className="h-3 w-3" />}
              </Button>
            ))}
          {isPro && (
            <Button variant="hero" size="sm" className="gap-2" onClick={handleExport}>
              <Download className="h-4 w-4" /> Full Pro report
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div
          className={cn("rounded-2xl border-2 p-6 shadow-card", riskConfig.border, riskConfig.bg)}
        >
          <div className="mb-3 flex items-center gap-2">
            <Scale className={cn("h-5 w-5", riskConfig.color)} />
            <span className={cn("text-xs font-bold uppercase tracking-wider", riskConfig.color)}>
              The Verdict · {riskConfig.label}
            </span>
          </div>
          <p className="text-base font-semibold leading-relaxed text-foreground">
            {result.verdict.summary}
          </p>

          {/* Risk score meter */}
          <div className="mt-5">
            <div className="mb-1.5 flex items-center justify-between text-xs">
              <span className="font-semibold uppercase tracking-wider text-muted-foreground">
                Risk score
              </span>
              <span className={cn("font-bold", riskConfig.color)}>{result.riskScore}/100</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  result.riskScore < 33
                    ? "bg-success"
                    : result.riskScore < 66
                      ? "bg-warning"
                      : "bg-destructive",
                )}
                style={{ width: `${Math.max(4, result.riskScore)}%` }}
              />
            </div>
          </div>

          {result.verdict.risk === "high" && (
            <div className="mt-5 flex flex-col items-center gap-3 border-t border-border/50 pt-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Talk to a real attorney
              </p>
              <Button variant="outline" size="sm" className="gap-2" onClick={handleLawyerReport}>
                <Download className="h-4 w-4" />
                Download lawyer-ready briefing
              </Button>
              <p className="text-[11px] text-muted-foreground text-center max-w-[280px]">
                One-page PDF with red flags, key terms & questions to ask — share it with any
                attorney.
              </p>
              <AvvoWidget
                source="analyzer-high-risk-verdict"
                riskScore={result.riskScore}
                riskLevel={result.verdict.risk}
                toolSlug={toolSlug ?? null}
              />
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary-glow" />
            <span className="text-xs font-bold uppercase tracking-wider text-primary-glow">
              Key Terms
            </span>
          </div>
          <ul className="space-y-3">
            {result.keyTerms.map((term, i) => (
              <li key={i} className="flex gap-2 text-sm leading-relaxed text-foreground">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-glow" />
                {term}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 shadow-card">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <span className="text-xs font-bold uppercase tracking-wider text-destructive">
              Red Flags ({result.redFlags.length})
            </span>
          </div>
          <ul className="space-y-3">
            {result.redFlags.map((flag, i) => (
              <li key={i} className="flex gap-2 text-sm leading-relaxed text-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                {flag}
              </li>
            ))}
            {result.redFlags.length === 0 && (
              <li className="text-sm text-muted-foreground">No red flags detected. Nice.</li>
            )}
          </ul>
        </div>
      </div>

      {/* Deep clause explanations */}
      {result.clauseExplanations.length > 0 && (
        <DeepClauses clauses={result.clauseExplanations} isPro={isPro} />
      )}
    </div>
  );
}

const sevConfig: Record<
  Severity,
  { color: string; bg: string; border: string; Icon: any; label: string }
> = {
  info: {
    color: "text-primary-glow",
    bg: "bg-primary/5",
    border: "border-primary/20",
    Icon: Info,
    label: "Standard",
  },
  caution: {
    color: "text-warning",
    bg: "bg-warning/10",
    border: "border-warning/30",
    Icon: AlertCircle,
    label: "Caution",
  },
  warning: {
    color: "text-destructive",
    bg: "bg-destructive/5",
    border: "border-destructive/30",
    Icon: AlertTriangle,
    label: "Watch out",
  },
};

function DeepClauses({
  clauses,
  isPro,
}: {
  clauses: AnalysisResult["clauseExplanations"];
  isPro: boolean;
}) {
  // Free users: show first 1 fully, blur the rest with upsell.
  const visibleCount = isPro ? clauses.length : Math.min(1, clauses.length);
  const hidden = clauses.length - visibleCount;

  return (
    <section className="mt-10">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold tracking-tight">Clause-by-clause breakdown</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            What each clause says, why it matters, and what to do.
          </p>
        </div>
        {!isPro && (
          <span className="hidden shrink-0 rounded-full bg-primary-glow/10 px-3 py-1 text-xs font-bold text-primary-glow sm:inline-block">
            Pro · Deep explanations
          </span>
        )}
      </div>

      <div className="space-y-4">
        {clauses.slice(0, visibleCount).map((c, i) => (
          <ClauseCard key={i} clause={c} />
        ))}

        {hidden > 0 && (
          <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div aria-hidden className="pointer-events-none space-y-3 p-6 blur-sm">
              {clauses.slice(visibleCount, visibleCount + 2).map((c, i) => (
                <ClauseCard key={i} clause={c} />
              ))}
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-t from-card via-card/95 to-card/40 p-6 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-glow shadow-elegant">
                <Crown className="h-6 w-6 text-primary-foreground" />
              </div>
              <p className="text-base font-bold">
                {hidden} more clause {hidden === 1 ? "explanation" : "explanations"} are Pro-only
              </p>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                Unlock the full breakdown — what each clause says, why it matters, and what to
                negotiate.
              </p>
              <Button variant="hero" size="lg" className="mt-4 gap-2" asChild>
                <Link to="/pricing">
                  Sign with confidence — Go Pro
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                7-day free trial · cancel anytime
              </p>
            </div>
          </div>
        )}
      </div>

      <p className="mt-6 text-center text-xs uppercase tracking-wider text-muted-foreground">
        Not legal advice. For informational purposes only.
      </p>
    </section>
  );
}

function ClauseCard({ clause }: { clause: AnalysisResult["clauseExplanations"][number] }) {
  const [copied, setCopied] = useState(false);
  const sev = sevConfig[clause.severity] ?? sevConfig.info;
  const Icon = sev.Icon;

  const handleCopy = () => {
    if (!clause.negotiationScript) return;
    navigator.clipboard.writeText(clause.negotiationScript);
    setCopied(true);
    toast.success("Script copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailDraft = () => {
    if (!clause.negotiationScript) return;
    const subject = `Proposed modification for ${clause.title}`;
    const body =
      `Hi,\n\n` +
      `I'd like to discuss a modification to the "${clause.title}" clause before signing.\n\n` +
      `Proposed wording / request:\n` +
      `"${clause.negotiationScript}"\n\n` +
      `Context — why this matters:\n${clause.why}\n\n` +
      `Happy to jump on a quick call if easier.\n\n` +
      `Thanks,\n`;
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    toast.success("Opening your email client…");
  };

  return (
    <div className={cn("rounded-2xl border-2 p-5 shadow-card", sev.border, sev.bg)}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Icon className={cn("h-4 w-4", sev.color)} />
        <h4 className="text-base font-bold">{clause.title}</h4>
        <span
          className={cn(
            "ml-auto rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
            sev.color,
            sev.bg,
          )}
        >
          {sev.label}
        </span>
      </div>
      <dl className="space-y-3 text-sm">
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            What it says
          </dt>
          <dd className="text-foreground/90 leading-relaxed">{clause.what}</dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Why it matters
          </dt>
          <dd className="text-foreground/90 leading-relaxed">{clause.why}</dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            What to do
          </dt>
          <dd className="font-medium text-foreground leading-relaxed">{clause.suggestion}</dd>
        </div>

        {clause.negotiationScript && clause.negotiationScript.trim() !== "" && (
          <div className="pt-2 border-t border-border/50">
            <dt className="mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <span>Negotiation Script</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] gap-1.5 hover:bg-background/50"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="h-3 w-3 text-success" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                  {copied ? "Copied" : "Copy script"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] gap-1.5 hover:bg-background/50"
                  onClick={handleEmailDraft}
                >
                  <Mail className="h-3 w-3" />
                  Email draft
                </Button>
              </div>
            </dt>
            <dd className="relative rounded-lg bg-background/50 p-3 font-mono text-[13px] leading-relaxed text-foreground/80 ring-1 ring-inset ring-border/50">
              "{clause.negotiationScript}"
            </dd>
          </div>
        )}
      </dl>
      {clause.severity === "warning" && <FindLawyerCta clauseTitle={clause.title} />}
    </div>
  );
}

function FindLawyerCta({ clauseTitle }: { clauseTitle: string }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    let clickId: string | null = null;
    try {
      const res = await logReferralClick({
        data: {
          partner: "avvo",
          source: "clause-card-warning",
          riskScore: null,
          riskLevel: "high",
          toolSlug: clauseTitle.slice(0, 80),
          referrerUrl: typeof window !== "undefined" ? window.location.href.slice(0, 2048) : null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 512) : null,
        },
      });
      clickId = res.click_id;
    } catch (err) {
      console.error("Failed to log referral click", err);
    }

    const url = new URL("https://www.avvo.com/find-a-lawyer");
    if (clickId) url.searchParams.set("subid", clickId);
    url.searchParams.set("utm_source", "legaldecoder");
    url.searchParams.set("utm_medium", "referral");
    url.searchParams.set("utm_campaign", "clause-card-warning");
    window.open(url.toString(), "_blank", "noopener,noreferrer");
    setLoading(false);
  };

  return (
    <div className="mt-4 rounded-xl border border-destructive/30 bg-background/60 p-3">
      <div className="flex items-start gap-3">
        <Scale className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div className="flex-1 text-sm">
          <p className="font-medium text-foreground leading-snug">
            This clause may violate your state's laws.
          </p>
          <p className="text-muted-foreground leading-snug">Need to speak to a local attorney?</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleClick}
          disabled={loading}
          className="shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10"
        >
          <MapPin className="h-3.5 w-3.5" />
          {loading ? "Opening…" : "Find a Lawyer via Avvo"}
        </Button>
      </div>
    </div>
  );
}
